import { apiFetch } from "./client";
import type { Page } from "./admin";

export const ACCESS_LEVELS = ["NO_ACCESS", "READ", "WRITE", "FULL"] as const;
export type AccessLevel = typeof ACCESS_LEVELS[number];
export type AccessTarget = "ROLE" | "USER";
export type AccessAction = { key: string; minimumLevel: AccessLevel; businessRestricted: boolean };
export type AccessSection = {
  code: string; sector: string; labelKey: string; supportedLevels: AccessLevel[];
  scope: string; internalOnly: boolean; actions: AccessAction[];
};
export type AccessCatalog = { version: number; sections: AccessSection[] };
export type AccessRole = {
  role: string; parentRole: string | null; internal: boolean; activeUsers: number; usersWithOverrides: number;
  inheritedActiveUsers?: number; lastChangedAt?: string | null;
};
export type AccessUser = {
  id: string; email: string; firstName: string; lastName: string; status: string;
  companyId: string | null; companyName: string | null; internal: boolean; roles: string[]; overrideCount: number;
};
export type AccessRow = {
  section: string; configuredLevel: AccessLevel | null; inheritedLevel: AccessLevel;
  effectiveLevel: AccessLevel; source: string; contributions: Record<string, AccessLevel>;
  supportedLevels: AccessLevel[]; locked: boolean; lockReason: string | null; scope: string;
  overrideReason?: string | null; overrideActorName?: string | null; overrideCreatedAt?: string | null;
  readScope?: string; writeScope?: string;
  dynamicSources?: { code: string; name: string | null; enabled: boolean }[];
  actionAccess?: { key: string; minimumLevel: AccessLevel; allowed: boolean; reason: string | null }[];
};
export type AccessPolicy = {
  version: number; targetType: AccessTarget; targetId: string; roles: string[];
  user?: AccessUser; rows: AccessRow[];
};
export type AccessChange = { section: string; operation: "SET_LEVEL" | "RESET_TO_INHERITED"; level?: AccessLevel };
export type AccessDiff = {
  section: string; operation: AccessChange["operation"]; previousLevel: AccessLevel | null;
  newLevel: AccessLevel | null; previousEffectiveLevel: AccessLevel; effectiveLevel: AccessLevel;
};
export type AccessPreviewRequest = {
  version: number; targetType: AccessTarget; targetId: string; changes: AccessChange[]; reason: string;
};
export type AccessPreview = {
  previewId: string; version: number; changedUsers: number; unchangedUsers: number; inactiveUsers: number;
  changes: AccessDiff[]; warnings: string[];
};
export type AccessImpact = {
  id: string; email: string; firstName: string; lastName: string; status: string; changed: boolean; reason: string;
  changes?: { section: string; beforeLevel: AccessLevel; afterLevel: AccessLevel; beforeReadScope: string; afterReadScope: string; beforeWriteScope: string; afterWriteScope: string }[];
};
export type AccessSave = { requestId: string; batchId: string; version: number; policy: AccessPolicy | null };
export type AccessHistory = {
  id: string; requestId: string; actorUserId: string; actorName: string; targetType: AccessTarget;
  targetId: string; targetName?: string; reason: string; versionBefore: number; versionAfter: number; createdAt: string; changes: AccessDiff[];
};
export type AccessUserFilters = { q?: string; role?: string; status?: string; internal?: string; companyId?: string; hasOverrides?: boolean };
export type AccessHistoryFilters = { targetType?: string; targetId?: string; section?: string; actorUserId?: string; from?: string; to?: string };

function query(values: Record<string, string | number | boolean | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  return `?${params}`;
}

export const accessApi = {
  catalog: () => apiFetch<AccessCatalog>("/admin/access/catalog"),
  roles: () => apiFetch<AccessRole[]>("/admin/access/roles"),
  policy: (type: AccessTarget, id: string) => apiFetch<AccessPolicy>(`/admin/access/${type === "ROLE" ? "roles" : "users"}/${encodeURIComponent(id)}`),
  users: (filters: AccessUserFilters, page: number, size: number) =>
    apiFetch<Page<AccessUser>>(`/admin/access/users${query({ ...filters, page, size })}`),
  companies: (q: string, page: number, size: number) =>
    apiFetch<Page<{ id: string; name: string }>>(`/admin/access/companies${query({ q, page, size })}`),
  preview: (body: AccessPreviewRequest) => apiFetch<AccessPreview>("/admin/access/preview", { method: "POST", body }),
  impact: (id: string, page: number, size: number) =>
    apiFetch<Page<AccessImpact>>(`/admin/access/previews/${encodeURIComponent(id)}/users${query({ page, size })}`),
  save: (previewId: string, requestId: string) =>
    apiFetch<AccessSave>("/admin/access/changes", { method: "POST", body: { previewId, requestId } }),
  request: (requestId: string) => apiFetch<AccessSave>(`/admin/access/changes/requests/${encodeURIComponent(requestId)}`),
  history: (filters: AccessHistoryFilters, page: number, size: number) =>
    apiFetch<Page<AccessHistory>>(`/admin/access/changes${query({ ...filters, page, size })}`),
};
